import React from 'react';

import * as mime from 'react-native-mime-types';
import { useTranslation } from 'react-i18next';
import './uploadInput.css';
import UploadRequest from "./uploadRequest";
import Icon from "../../ui/icon/icon";
import Button from "../../ui/button/button";
import UploadInfos from "./components/uploadInfos";
import {UUID} from "../../../../utils/utils";
import InputErrors, {withErrors} from "../../ui/inputErrors/inputErrors";
import ModalEditImage from './components/modalEditImage';

export function UploadTrigger({
    onFileSelected = () => {},
    acceptedMimeTypes = [],
    disabled = false,
    isUploading = false,
    label = null,
    errors = null,
    uniqueKey,
}) {
    const { t } = useTranslation();

    const fileInputRef = React.useRef(null);

    const inputId = React.useMemo(() => {
        return `imageBtn_${uniqueKey}`
    }, [uniqueKey]);

    const labelId = React.useMemo(() => {
        return `label_${uniqueKey}`
    }, [uniqueKey]);

    const triggerId = React.useMemo(() => {
        return `triggerBtn_${uniqueKey}`
    }, [uniqueKey]);

    const onChange = React.useCallback(({ target }) => {
        onFileSelected(target.files[0])
    }, [onFileSelected]);

    const getKey = (element = '') => uniqueKey ? `field_${uniqueKey}${element ? '_' + element : ''}` : null;

    return (
        <div className={'neo-upload-trigger'}>
            <input className="neo-file-input" id={inputId} ref={fileInputRef} hidden disabled={disabled} type={'file'} accept={acceptedMimeTypes.join(', ')} onChange={onChange} />
            <Button
                id={triggerId}
                aria-describedby={`${getKey('description')} ${getKey('constraints')} ${getKey('error')}, ${labelId}`}
                aria-disabled={disabled}
                aria-controls={inputId}
                tabIndex="0"
                fluid
                onClick={() => {fileInputRef?.current?.click()}}
                type='inverted-secondary'
                disabled={disabled || isUploading}
                className={withErrors(errors) ? 'error' : ''}
                label={
                    <span id={labelId} className={'neo-upload-trigger_label'} aria-hidden={true}>
                        <Icon name='upload'/>
                        <span>{label || t('widget.input.upload.button_label')}</span>
                    </span>
                }
            />
        </div>
    )
};


async function getFileFromUrl(path, url_preview, defaultType = 'image/jpeg'){
    if (!path) {
        return;
    }
    const pathSplitted = path.split('/')
    const name = pathSplitted[pathSplitted.length - 1];
    const extension = name.split(".")[1]
    const response = await fetch(url_preview);
    if (!response.ok) {
        throw new Error(`request on ${url_preview} failed with ${response.status}`);
    }
    const data = await response.blob();
    return new File([data], name, {
        type: mime.lookup(`.${extension}`) || defaultType,
    });
}

export default function UploadInput({
    url_preview = null,
    disabled = false,
    onChange = () => {},
    value = null,
    fileTypes = null,
    labelButton = null,
    label = null,
    description = null,
    required = false,
    maxSize = 2097152,
    className = '',
    uploadClass = UploadRequest,
    errors = null,
    upload_config = null,
    subtype = "",
    uniqueKey,
    display = true,
    cropModalProps = {
        ratio: null,
        forceRatio: false,
        minWidth: 180,
        maxWidth: Infinity,
        minHeight: 180,
        maxHeight: Infinity
    }
}) {

    const _cropModalProps = React.useMemo(() => {
        if (subtype === 'image') {
            return {
                ratio: 7/9,
                forceRatio: true,
                minWidth: 180,
                maxWidth: Infinity,
                minHeight: 180,
                maxHeight: Infinity
            }
        }
        return cropModalProps;
    }, [subtype]);

    const [editImage, setEditImage] = React.useState(null);
    const [file, setFile] = React.useState(null);
    const [fileErrors, setFileErrors] = React.useState([]);
    const [isUploading, setIsUploading] = React.useState(false);
    const uuidFileName = UUID();
    const { t } = useTranslation();

    const _fileTypes = React.useMemo(() => {
        if (!fileTypes)
            return subtype === 'image' ? UploadInput.imageFileTypes : UploadInput.defaultFileTypes;
        return fileTypes;
    },[fileTypes, subtype]);

    const acceptedMimeTypes = React.useMemo(() => {
        return _fileTypes.map(value => mime.lookup(value));
    },[_fileTypes]);

    const isImage = React.useMemo(() => {
        if (file?.type) {
            return file.type.startsWith("image/");
        }

        return false;
    }, [file]);

    const uploadStatus = React.useMemo(() => {
        switch (true){
            case Boolean(isUploading):
                return 'uploading';
            case Boolean(fileErrors.length):
                return 'error'
            case Boolean(value):
                return 'success';
            default:
                return null
        }
    }, [value, fileErrors, isUploading]);

    const errorsWording = {
        fileInvalidType : t('widget.input.upload.file_invalid_type'),
        fileTooLarge: t('widget.input.upload.file_too_large', { maxsize: Math.trunc(maxSize / 1000000) }),
        failed: t('widget.input.upload.failed')
    };

    const onCancel = React.useCallback(() => {
        const imagebtn = document.getElementById(`imageBtn_${uniqueKey}`);
        if (imagebtn)
            imagebtn.value = null;
    }, [uniqueKey]);

    const focusTrigger = React.useCallback(() => {
        document?.getElementById(`triggerBtn_${uniqueKey}`)?.focus();
    }, [uniqueKey]);

    const _onUpload = React.useCallback((file) => {
            const uploadRequest = new uploadClass(upload_config);

            setIsUploading(true);
            uploadRequest.uploadFile(file, uuidFileName)
                .then(( response ) => {
                    onChange(`${upload_config.base_url_bucket}/${response}`);
                })
                .catch((e) => {
                    console.error(e)
                    setFileErrors([errorsWording['failed']]);
                    onChange(null)
                })
                .finally(() => {
                    setIsUploading(false);
                    setEditImage(null);
                    focusTrigger();
                });
        },
        [onChange, uploadClass]
    );

    const onFileSelected = React.useCallback(
        (file) => {
            onCancel();
            if (!file) {
                return;
            }
            let _fileErrors = [];
            if (!acceptedMimeTypes.includes(file.type)) {
                _fileErrors.push(errorsWording['fileInvalidType']);
            }
            if (file.size > maxSize) {
                _fileErrors.push(errorsWording['fileTooLarge']);
            }
            setFileErrors(_fileErrors);

            setFile(file);

            if (_fileErrors.length) {
                onChange(null)
                return;
            }
            const extension = file.name.substring(file.name.lastIndexOf('.'));
            if (UploadInput.imageFileTypes.includes(extension.toLowerCase())) {
                return setEditImage(file);
            }
            _onUpload(file);
        },
        [_onUpload, acceptedMimeTypes, maxSize, onChange, onCancel]
    );

    const onFileSelectedModal = React.useCallback(
        (file) => {
            onCancel();
            if (!file) {
                return;
            }
            const extension = file.name.substring(file.name.lastIndexOf('.'));
            if (UploadInput.imageFileTypes.includes(extension.toLowerCase())) {
                setEditImage(file);
            }
            let _fileErrors = [];
            if (!acceptedMimeTypes.includes(file.type)) {
                _fileErrors.push(errors['fileInvalidType']);
            }
            if (file.size > maxSize) {
                _fileErrors.push(errors['fileTooLarge']);
            }
            setFileErrors(_fileErrors);

            setFile(file);

            if (_fileErrors.length) {
                onChange(null)
                return;
            }
            if (!UploadInput.imageFileTypes.includes(extension.toLowerCase())) {
                _onUpload(file);
            }
        },
        [_onUpload, acceptedMimeTypes, maxSize, onChange]
    );

    React.useEffect(() => {
        //Load file from value
        if (url_preview && value && !file) {
            getFileFromUrl(value, url_preview)
                .then(f => {
                    setFile(f);
                })
                .catch(e => {
                    console.error(e);
                    setFileErrors([errorsWording['failed']]);
                });
        }
    },[value, file, url_preview])

    if (!upload_config || !display) {
        return null;
    }

    const getKey = (element = '') => uniqueKey ? `field_${uniqueKey}${element ? '_' + element : ''}` : null;

    return (
        <>
            <ModalEditImage
                isOpen={Boolean(editImage)}
                imageFile={editImage}
                isSubmitting={isUploading}
                onClose={() => {setEditImage(null); onCancel(); focusTrigger()}}
                onSubmit={_onUpload}
                onSelectFile={onFileSelectedModal}
                maxSize={maxSize}
                fileErrors={fileErrors}
                acceptedMimeTypes={acceptedMimeTypes}
                isUploading={isUploading}
                uniqueKey={uniqueKey}
                {..._cropModalProps}
            />
            <div className={`neo-upload-container ${className}`}>
                {label && <label className='neo-input-label' for={`triggerBtn_${uniqueKey}`}>{label} {required && <span aria-label={t('widget.required-field')} className='neo-input-required'>*</span>}</label>}
                {description && <p className='neo-input-description' id={getKey('description')}>{description}</p>}
                <p className='neo-upload-label_constraints' id={getKey('constraints')}>
                    {t('widget.input.upload.label_constraints_upload', {acceptedExtensions: _fileTypes.join(', '), maxSize: Math.trunc(maxSize / 1000000)})}
                </p>
                <div className="upload-zone-file-content">
                    <UploadTrigger
                        onFileSelected={onFileSelected}
                        acceptedMimeTypes={acceptedMimeTypes}
                        disabled={disabled}
                        maxSize={maxSize}
                        label={labelButton}
                        isUploading={isUploading}
                        errors={errors}
                        uniqueKey={uniqueKey}
                    />
                    <UploadInfos
                        errors={fileErrors}
                        file={file}
                        status={uploadStatus}
                        upload={onFileSelected}
                        isImage={isImage}
                    />
                    <InputErrors errors={errors} id={getKey('error')}/>
                </div>
            </div>
        </>
    );
}

UploadInput.imageFileTypes = ['.jpg', '.jpeg', '.png'];
UploadInput.defaultFileTypes = ['.jpg', '.jpeg', '.png','.pdf'];
